/**
 * Domains — give an application an address of its own.
 *
 * The screen exists to make one thing obvious: a hostname is not served
 * because it was typed here. It is served once DNS shows it pointed at this
 * platform (or carrying the verification value), which is why the records to
 * create are shown before anything else and why a domain sits visibly
 * "pending" until the check passes. The failure text is the DNS answer we
 * actually got — "it is not working" is not something an administrator can
 * act on.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AlertTriangle, Check, Copy, Globe, Loader2, Lock, Pencil, RefreshCw, Star, Trash2,
} from 'lucide-react'
import {
  addDomain, getDomainConfig, listDomains, makeDomainPrimary, removeDomain,
  renameDomain, retryDomainCertificate, setDomainEnabled, verifyDomain,
  type DomainConfig, type DomainTls, type ProjectDomain,
} from '@/api/domains'

const STATUS_LABEL: Record<ProjectDomain['status'], string> = {
  pending: 'Waiting for DNS',
  active: 'Live',
  disabled: 'Suspended',
}

const STATUS_COLOR: Record<ProjectDomain['status'], string> = {
  pending: 'var(--color-warning, #d97706)',
  active: 'var(--color-success, #16a34a)',
  disabled: 'var(--color-text-secondary)',
}

/**
 * Where the link on this screen should actually take you.
 *
 * The server's `url` is the application's canonical public address, which is
 * what an email says. A link on THIS page should open the application in the
 * environment the person is looking at it from — same scheme, same port — or
 * a click on a development box lands on `https://…` with no port and fails.
 */
function openUrl(domain: ProjectDomain): string {
  const port = window.location.port ? `:${window.location.port}` : ''
  return `${window.location.protocol}//${domain.hostname}${port}`
}

function errText(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

export default function DomainsPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()

  const [config, setConfig] = useState<DomainConfig | null>(null)
  const [domains, setDomains] = useState<ProjectDomain[]>([])
  const [zone, setZone] = useState('')
  const [availableSites, setAvailableSites] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)

  const [hostname, setHostname] = useState('')
  const [target, setTarget] = useState<'app' | 'site'>('app')
  const [siteSlug, setSiteSlug] = useState('')

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      const [cfg, list] = await Promise.all([
        getDomainConfig().catch(() => null),
        listDomains(project),
      ])
      setConfig(cfg)
      setDomains(list.domains)
      setZone(list.zone)
      setAvailableSites(list.available_sites)
    } catch (err) {
      toast.error(errText(err, 'Could not load domains'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])

  useEffect(() => { document.title = `Domains · ${project}` }, [project])

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hostname.trim()) return
    setAdding(true)
    try {
      const created = await addDomain(project, {
        hostname: hostname.trim(),
        target,
        site_slug: target === 'site' ? siteSlug.trim() || null : null,
      })
      setDomains((d) => [...d, created])
      setHostname('')
      setSiteSlug('')
      toast.success(
        created.status === 'active'
          ? `${created.hostname} is live`
          : `${created.hostname} added — create the DNS records below, then check it`,
      )
    } catch (err) {
      toast.error(errText(err, 'Could not add that domain'))
    } finally {
      setAdding(false)
    }
  }

  const replace = (d: ProjectDomain) =>
    setDomains((list) => list.map((x) => (x.id === d.id ? d : x)))

  const act = async (id: number, fn: () => Promise<void>) => {
    setBusyId(id)
    try { await fn() } finally { setBusyId(null) }
  }

  const onVerify = (d: ProjectDomain) => act(d.id, async () => {
    try {
      const next = await verifyDomain(d.id)
      replace(next)
      if (next.status === 'active') toast.success(`${next.hostname} is live`)
      else toast(next.last_error || 'DNS does not show this domain yet')
    } catch (err) {
      toast.error(errText(err, 'Could not check that domain'))
    }
  })

  const onRetryCert = (d: ProjectDomain) => act(d.id, async () => {
    try {
      replace(await retryDomainCertificate(d.id))
      toast.success('Certificate requested — it should be ready within a minute or two')
    } catch (err) {
      toast.error(errText(err, 'Could not request a certificate'))
    }
  })

  const onPrimary = (d: ProjectDomain) => act(d.id, async () => {
    try {
      const next = await makeDomainPrimary(d.id)
      // One primary at a time: the server cleared the others, so the list has
      // to agree or two rows would wear the badge until the next load.
      setDomains((list) => list.map((x) => (
        x.id === next.id ? next : { ...x, is_primary: false }
      )))
      toast.success(`${next.hostname} is the main address`)
    } catch (err) {
      toast.error(errText(err, 'Could not set the main address'))
    }
  })

  const onToggle = (d: ProjectDomain) => act(d.id, async () => {
    try {
      replace(await setDomainEnabled(d.id, d.status !== 'active'))
    } catch (err) {
      toast.error(errText(err, 'Could not change that domain'))
    }
  })

  const onRename = (d: ProjectDomain, label: string) => act(d.id, async () => {
    try {
      const next = await renameDomain(d.id, label)
      replace(next)
      toast.success(`Now at ${next.hostname}`)
    } catch (err) {
      toast.error(errText(err, 'Could not change that address'))
      throw err   // keeps the editor open on the value the person typed
    }
  })

  const onRemove = (d: ProjectDomain) => act(d.id, async () => {
    if (!window.confirm(
      `Stop serving this application at ${d.hostname}?\n\n`
      + 'Anyone using that address will no longer reach it. The DNS records '
      + 'you created stay yours to remove or re-point.',
    )) return
    try {
      await removeDomain(d.id)
      setDomains((list) => list.filter((x) => x.id !== d.id))
      toast.success(`${d.hostname} removed`)
    } catch (err) {
      toast.error(errText(err, 'Could not remove that domain'))
    }
  })

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success('Copied')
    } catch {
      // Clipboard access is denied in some browsers/contexts; the value is on
      // screen either way.
      toast('Select the value to copy it')
    }
  }

  return (
    <div style={{ padding: '0 32px 32px', maxWidth: 940 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/workbench')}>
          &larr; Back
        </button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>
          Domains: {project}
        </h1>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 20px 44px' }}>
        This application already has an address of its own — named after the
        project, on this platform&apos;s own domain, working now with nothing to
        set up. Rename it to whatever suits you, and add a domain you own
        (<code>erp.acme.com</code>) alongside it. At any of them the application
        is the whole site: no builder, no other project, just this one.
      </p>

      {config && !config.configured && (
        <div className="card" style={{
          padding: 14, marginBottom: 18, display: 'flex', gap: 10,
          borderLeft: '3px solid var(--color-warning, #d97706)',
        }}>
          <AlertTriangle size={18} style={{ color: 'var(--color-warning, #d97706)', flexShrink: 0 }} />
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            This platform is running at an address a domain cannot be pointed
            at ({config.platform_host || 'no public hostname'}), so there is no
            traffic record to show you. You can still add a domain and verify
            it with a TXT record — it will serve as soon as the platform is
            reachable at a public name.
          </div>
        </div>
      )}

      {/* Add a domain the customer owns. The platform address above is not
          added here — every project has one already. */}
      <form className="card" onSubmit={onAdd} style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
          Add a domain you own
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 260px' }}>
            <label className="form-label" htmlFor="domain-host">Hostname</label>
            <input
              id="domain-host"
              className="form-input"
              placeholder="erp.acme.com"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label className="form-label" htmlFor="domain-target">Serves</label>
            <select
              id="domain-target"
              className="form-input"
              value={target}
              onChange={(e) => setTarget(e.target.value as 'app' | 'site')}
            >
              <option value="app">The application</option>
              <option value="site">A published site</option>
            </select>
          </div>
          {target === 'site' && (
            <div style={{ flex: '1 1 160px' }}>
              <label className="form-label" htmlFor="domain-slug">Site</label>
              {availableSites.length > 0 ? (
                <select
                  id="domain-slug"
                  className="form-input"
                  value={siteSlug}
                  onChange={(e) => setSiteSlug(e.target.value)}
                >
                  <option value="">— choose a site —</option>
                  {availableSites.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              ) : (
                <input
                  id="domain-slug"
                  className="form-input"
                  placeholder="main"
                  value={siteSlug}
                  onChange={(e) => setSiteSlug(e.target.value)}
                />
              )}
            </div>
          )}
          <button className="btn btn-primary" disabled={adding || !hostname.trim()}>
            {adding ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Globe size={15} />}
            <span style={{ marginLeft: 6 }}>Add domain</span>
          </button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 10 }}>
          {config?.cname
            ? <>You will be asked to point it at <code>{config.cname}</code>.</>
            : null}
          {config?.suffix && (
            <> A name ending in <code>{config.suffix}</code> belongs to this
              platform and goes live immediately — there is nothing to verify.</>
          )}
        </div>
      </form>

      {loading ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--color-text-secondary)' }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
        </div>
      ) : domains.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          This platform has no public address of its own, so there is no name it
          can give this application. Add a domain you own above — it will serve
          as soon as the platform is reachable at a public name.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {domains.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              busy={busyId === d.id}
              zone={zone}
              onVerify={() => onVerify(d)}
              onPrimary={() => onPrimary(d)}
              onToggle={() => onToggle(d)}
              onRemove={() => onRemove(d)}
              onRename={(label) => onRename(d, label)}
              onRetryCert={() => onRetryCert(d)}
              onCopy={copy}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The HTTPS badge: what the edge (Instance Admin → Edge & HTTPS) says about
 * this host's certificate. Nothing is shown when HTTPS is handled outside the
 * platform — the screen cannot know, and must not claim either way.
 */
function tlsBadge(tls: DomainTls | undefined): { text: string; color: string; title: string } | null {
  if (!tls || !tls.managed) return null
  const until = tls.not_after
    ? new Date(tls.not_after.endsWith('Z') ? tls.not_after : `${tls.not_after}Z`).toLocaleDateString()
    : ''
  switch (tls.status) {
    case 'active':
      return { text: until ? `HTTPS · until ${until}` : 'HTTPS', color: 'var(--color-success)', title: 'Certificate in service; renewed automatically' }
    case 'pending':
    case 'issuing':
    case 'none':
      return { text: 'Securing…', color: 'var(--color-info)', title: 'A certificate is being issued — usually within a minute or two' }
    case 'failed':
      return { text: 'Certificate failed', color: 'var(--color-error)', title: tls.last_error || '' }
    case 'cloudflare': {
      const cf = tls.cloudflare_ssl_status || 'pending'
      return cf === 'active'
        ? { text: 'HTTPS · Cloudflare', color: 'var(--color-success)', title: 'Certificate issued by Cloudflare' }
        : { text: `Cloudflare: ${cf.replace(/_/g, ' ')}`, color: 'var(--color-info)', title: 'Cloudflare is issuing the certificate' }
    }
    default:
      return null
  }
}

function DomainCard({ domain, zone, busy, onVerify, onPrimary, onToggle, onRemove, onRename, onRetryCert, onCopy }: {
  domain: ProjectDomain
  zone: string
  busy: boolean
  onVerify: () => void
  onPrimary: () => void
  onToggle: () => void
  onRemove: () => void
  onRename: (label: string) => Promise<void>
  onRetryCert: () => void
  onCopy: (v: string) => void
}) {
  const live = domain.status === 'active'
  const tls = live ? tlsBadge(domain.tls) : null
  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState(domain.label)
  useEffect(() => { setLabel(domain.label) }, [domain.label])
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Globe size={18} style={{ color: 'var(--color-accent)' }} />
        <a
          href={openUrl(domain)}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text)' }}
        >
          {domain.hostname}
        </a>
        <span style={{
          fontSize: 13, padding: '2px 8px', borderRadius: 10,
          border: `1px solid ${STATUS_COLOR[domain.status]}`,
          color: STATUS_COLOR[domain.status],
        }}>
          {STATUS_LABEL[domain.status]}
        </span>
        {live && domain.tls?.propagation && domain.tls.propagation.servers_total > 0 && (() => {
          const p = domain.tls.propagation
          const all = p.servers_ok === p.servers_total
          const color = all ? 'var(--color-success)' : 'var(--color-error)'
          return (
            <span
              title={all ? 'The latest change to this domain has reached every web server'
                : 'Some web servers have not received the latest change yet — see below'}
              style={{ fontSize: 13, padding: '2px 8px', borderRadius: 10, border: `1px solid ${color}`, color }}
            >
              {all
                ? (p.servers_total === 1 ? 'On the web server' : `On all ${p.servers_total} servers`)
                : `On ${p.servers_ok} of ${p.servers_total} servers`}
            </span>
          )
        })()}
        {tls && (
          <span title={tls.title} style={{
            fontSize: 13, padding: '2px 8px', borderRadius: 10,
            border: `1px solid ${tls.color}`, color: tls.color,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Lock size={11} /> {tls.text}
          </span>
        )}
        {domain.is_default && (
          <span
            title="Named after the project, on this platform's own domain — nothing to set up"
            style={{
              fontSize: 13, padding: '2px 8px', borderRadius: 10,
              background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
            }}
          >
            Platform address
          </span>
        )}
        {domain.is_primary && (
          <span
            title="Links in this application's emails use this address"
            style={{
              fontSize: 13, padding: '2px 8px', borderRadius: 10,
              background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Star size={11} /> Main address
          </span>
        )}
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          {domain.target === 'site'
            ? `published site${domain.site_slug ? ` · ${domain.site_slug}` : ''}`
            : 'the application'}
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {domain.is_default ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing((v) => !v)}
                    disabled={busy} title="Use a different name on this platform">
              <Pencil size={14} /><span style={{ marginLeft: 4 }}>Change name</span>
            </button>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={onVerify} disabled={busy}
                    title="Ask DNS again">
              {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />}
              <span style={{ marginLeft: 4 }}>{live ? 'Re-check' : 'Check DNS'}</span>
            </button>
          )}
          {live && !domain.is_primary && domain.target === 'app' && (
            <button className="btn btn-ghost btn-sm" onClick={onPrimary} disabled={busy}
                    title="Use this address in this application's emails">
              <Star size={14} /><span style={{ marginLeft: 4 }}>Make main</span>
            </button>
          )}
          {domain.verified_at && (
            <button className="btn btn-ghost btn-sm" onClick={onToggle} disabled={busy}>
              {live ? 'Suspend' : 'Enable'}
            </button>
          )}
          {/* The platform address is not removable: every project has one, and
              taking it away would leave an application with no address at all
              until somebody bought a domain. Suspend it instead. */}
          {!domain.is_default && (
            <button className="btn btn-ghost btn-sm" onClick={onRemove} disabled={busy}
                    title="Stop serving this application here">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {editing && domain.is_default && (
        <form
          style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
          onSubmit={(e) => {
            e.preventDefault()
            onRename(label.trim()).then(() => setEditing(false)).catch(() => { /* stays open */ })
          }}
        >
          <input
            className="form-input"
            style={{ maxWidth: 220 }}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoFocus
            spellCheck={false}
            aria-label="New name"
          />
          <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>{zone}</span>
          <button className="btn btn-primary btn-sm" disabled={busy || !label.trim() || label === domain.label}>
            Change
          </button>
          <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => { setLabel(domain.label); setEditing(false) }}>
            Cancel
          </button>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', flexBasis: '100%' }}>
            Letters, numbers and hyphens. The old address stops working as soon
            as you change it.
          </span>
        </form>
      )}

      {domain.last_error && !live && (
        <div style={{
          marginTop: 10, fontSize: 13, lineHeight: 1.5,
          color: 'var(--color-text-secondary)',
          display: 'flex', gap: 8,
        }}>
          <AlertTriangle size={14} style={{ color: 'var(--color-warning, #d97706)', flexShrink: 0, marginTop: 1 }} />
          <span>{domain.last_error}</span>
        </div>
      )}

      {live && domain.tls?.propagation && domain.tls.propagation.servers_ok < domain.tls.propagation.servers_total && (
        <div style={{
          marginTop: 10, fontSize: 13, lineHeight: 1.5,
          color: 'var(--color-text-secondary)', display: 'flex', gap: 8,
        }}>
          <AlertTriangle size={14} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: 1 }} />
          <span>
            The latest change to this domain has not reached every web server yet. Visitors sent
            to those servers may see an old certificate or no site. Your platform administrators
            are notified and the change is retried automatically.
            <ul style={{ margin: '4px 0 0', paddingLeft: '1.1rem' }}>
              {domain.tls.propagation.servers.filter((x) => !x.ok).map((x) => (
                <li key={x.name}><strong>{x.name}</strong> ({x.status}){x.error ? `: ${x.error.split('\n')[0]}` : ''}</li>
              ))}
            </ul>
          </span>
        </div>
      )}

      {live && domain.tls?.status === 'failed' && (
        <div style={{
          marginTop: 10, fontSize: 13, lineHeight: 1.5,
          color: 'var(--color-text-secondary)',
          display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={14} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: 1 }} />
          <span style={{ flex: 1 }}>
            HTTPS certificate could not be issued: {domain.tls.last_error || 'no reason given'}
            {domain.tls.next_attempt_at && (
              <> It will be tried again automatically at{' '}
                {new Date(domain.tls.next_attempt_at.endsWith('Z') ? domain.tls.next_attempt_at : `${domain.tls.next_attempt_at}Z`).toLocaleString()}.</>
            )}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onRetryCert} disabled={busy}>
            <RefreshCw size={14} /><span style={{ marginLeft: 4 }}>Retry now</span>
          </button>
        </div>
      )}

      {domain.dns.length > 0 && !live && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Create one of these records with your DNS provider
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {domain.dns.map((r, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '54px 1fr 1fr 28px',
                gap: 8, alignItems: 'center', fontSize: 13,
                background: 'var(--color-bg-secondary)', padding: '6px 8px', borderRadius: 6,
              }}>
                <span style={{ fontWeight: 600 }}>{r.kind}</span>
                <code style={{ overflowWrap: 'anywhere' }}>{r.name}</code>
                <code style={{ overflowWrap: 'anywhere' }}>{r.value}</code>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ padding: 2 }}
                  onClick={() => onCopy(r.value)}
                  title="Copy the value"
                >
                  <Copy size={13} />
                </button>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 6 }}>
            The traffic record (CNAME or A) both points visitors here and proves
            the domain is yours. The TXT record proves it without moving any
            traffic — use it to verify a domain that is still live elsewhere.
          </div>
        </div>
      )}

      {live && (
        <div style={{
          marginTop: 10, fontSize: 13, color: 'var(--color-text-secondary)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Check size={14} style={{ color: STATUS_COLOR.active }} />
          {domain.is_default
            ? 'On this platform\u2019s own domain — nothing to configure, working now.'
            : <>Verified{domain.verification_method ? ` by ${domain.verification_method.toUpperCase()} record` : ''}
                {domain.verified_at ? ` on ${new Date(domain.verified_at).toLocaleDateString()}` : ''}.</>}
        </div>
      )}
    </div>
  )
}
